import { config } from '@/forms/config';
import type {
  FormConditionalValidation,
  FormField,
  FormInitialValues,
  FormValidations,
  QuestionWithDetailsFormField,
} from '@/forms/typings';
import { FormFieldType } from '@/forms/typings';
import { generateValidations } from '@/forms/utils/generateValidations';

import type { FormFieldModuleContext } from '../registry/field-module.types';

export function buildQuestionWithDetailsInitialState(
  field: FormField,
  values: FormInitialValues,
  validations: FormValidations,
  conditions: FormConditionalValidation[] | undefined,
  ctx: FormFieldModuleContext,
): { initialValues: FormInitialValues; validationFields: FormValidations } {
  let initialValues: FormInitialValues = { ...values };
  let validationFields: FormValidations = { ...validations };

  const f = field as QuestionWithDetailsFormField;

  if (f.notApplicableField) {
    initialValues[f.notApplicableField.fieldId] = f.notApplicableField.value;
  }

  const shouldValidateFieldId = `${f.fieldId}${config.shouldValidateFieldSuffix}`;
  initialValues[shouldValidateFieldId] =
    f.answer !== undefined && !f.notApplicableField?.value;

  initialValues[`${f.fieldId}${config.questionWithDetailsFieldSuffix}`] =
    f.answer === undefined ? '' : f.answer ? 'yes' : 'no';

  initialValues[`${f.fieldId}${config.requiredFieldSuffix}`] = false;

  const schema = generateValidations(f, conditions);

  if (schema) {
    validationFields[`${f.fieldId}${config.requiredFieldSuffix}`] = schema;
  }

  if (f.trueDetails) {
    const combinedTrueConditions: FormConditionalValidation[] = [
      {
        type: 'should-validate',
        relatedFieldId: shouldValidateFieldId,
      },
      {
        type: FormFieldType.QuestionWithDetails,
        relatedFieldId: `${f.fieldId}${config.questionWithDetailsFieldSuffix}`,
        on: 'yes',
      },
    ];

    if (conditions) {
      combinedTrueConditions.push(...conditions);
    }

    const detailValuesAndValidations = ctx.recurse(
      f.trueDetails,
      initialValues,
      validationFields,
      combinedTrueConditions,
    );

    initialValues = {
      ...initialValues,
      ...detailValuesAndValidations.initialValues,
    };

    validationFields = {
      ...validationFields,
      ...detailValuesAndValidations.validationFields,
    };
  }

  if (f.falseDetails) {
    const combinedFalseConditions: FormConditionalValidation[] = [
      {
        type: 'should-validate',
        relatedFieldId: shouldValidateFieldId,
      },
      {
        type: FormFieldType.QuestionWithDetails,
        relatedFieldId: `${f.fieldId}${config.questionWithDetailsFieldSuffix}`,
        on: 'no',
      },
    ];

    if (conditions) {
      combinedFalseConditions.push(...conditions);
    }

    const detailValuesAndValidations = ctx.recurse(
      f.falseDetails,
      initialValues,
      validationFields,
      combinedFalseConditions,
    );

    initialValues = {
      ...initialValues,
      ...detailValuesAndValidations.initialValues,
    };

    validationFields = {
      ...validationFields,
      ...detailValuesAndValidations.validationFields,
    };
  }

  if (f.matchingDetails && f.matchingAnswer !== undefined) {
    const combinedMatchingConditions: FormConditionalValidation[] = [
      {
        type: FormFieldType.QuestionWithDetails,
        relatedFieldId: `${f.fieldId}${config.questionWithDetailsFieldSuffix}`,
        on: f.matchingAnswer ? 'yes' : 'no',
      },
    ];

    if (conditions) {
      combinedMatchingConditions.push(...conditions);
    }

    const detailValuesAndValidations = ctx.recurse(
      f.matchingDetails,
      initialValues,
      validationFields,
      combinedMatchingConditions,
    );

    initialValues = {
      ...initialValues,
      ...detailValuesAndValidations.initialValues,
    };

    validationFields = {
      ...validationFields,
      ...detailValuesAndValidations.validationFields,
    };
  }

  if (f.naDetails && f.notApplicableField) {
    const combinedNAConditions: FormConditionalValidation[] = [
      { type: 'not-applicable', relatedFieldId: f.notApplicableField.fieldId },
    ];

    if (conditions) {
      combinedNAConditions.push(...conditions);
    }

    const naValuesAndValidations = ctx.recurse(
      f.naDetails,
      initialValues,
      validationFields,
      combinedNAConditions,
    );

    initialValues = {
      ...initialValues,
      ...naValuesAndValidations.initialValues,
    };

    validationFields = {
      ...validationFields,
      ...naValuesAndValidations.validationFields,
    };
  }

  return { initialValues, validationFields };
}
